import { Icon } from '@iconify/react';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  InputAdornment,
  IconButton,
  Typography,
  Grid,
  Box,
  Paper,
  Tabs,
  Tab,
  Stack
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import plusFill from '@iconify/icons-eva/plus-fill';
import { useTranslation } from 'react-i18next';
import ObjectTable from './objecttable';
import WaitComponent from './WaitComponent';
import {addChecksum27, validateEmail, dateWithDayOffset} from './../common';
import configData from "../config.json";

// Helper to generate a unique ID for new orders
const generateUid = () => {
  const date = new Date();
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  const milliseconds = Math.ceil(date.getMilliseconds() / 100);
  
  let pbl = window.orders_new_pbl ? "PBL" : "";
  
  return `${pbl}${year}${month}${day}${hours}${minutes}${seconds}${milliseconds}`;
};

var order_editor_setting_suggest = false;

// Global function placeholder for warehouses (replace with actual implementation)
window.hpay_get_warehouses = window.hpay_get_warehouses || (() => ({
  "default": { "Uid": "default", "Name": "Main Warehouse 1", "Country": "Serbia", "City": "Belgrade", "PostalCode": "21000", "StreetName": "Dusana Petrovica Saneta", "StreetNumber": "26A" },
  "skladiste2": { "Uid": "skladiste2", "Name": "Our Small Second Warehouse", "Country": "Serbia", "City": "Belgrade", "PostalCode": "11000", "StreetName": "Teslina", "StreetNumber": "26A" }
}));

const PRODUCT_SUGGESTIONS_KEY = 'order_editor_suggest';

export default function OrderEditor({ order, onEditedOrderData, SitePaymentMethods, SiteShippingMethods, SiteFiscalMethods }) {
  const { t } = useTranslation();
  
  const [locked, setLocked] = useState(() => {
	  try{
		  if(order && order.id) {
			 if(order.Data && order.Data.sitedata && !order.Data.hpayedit){
				 return true;
			 }  
		  }
		  return false;
	  }catch(ex){
		  return false;
	  }
  });
  
  const isPayByLink = useMemo(() => {
	  if(window.orders_new_pbl){
		  return true;
	  }else if(order && order.Data && order.Data.is_pbl){
		  return true;
	  }else
		  return false;
  },[order]);
  
  const EMPTY_ORDER = {
			id: null,
			Uid: generateUid(),
			CompanySiteId: sessionStorage.getItem('site_id') ? parseInt(sessionStorage.getItem('site_id')) : null,
			PaymentMethodId: null,
			CompanySitePaymentMethodId: null,
			SubscriptionId: null,
			Name: `#${generateUid()}`,
			Amount: 0,
			Currency: 'EUR',
			ChargedAt: null,
			Status: '', // Default status is empty string for new orders
			Data: {
			  billing: {
				email: '',
				first_name: '',
				last_name: '',
				phone: '',
				is_company: 0,
				company: '',
				company_tax_id: '',
				company_reg_id: '',
				address: '',
				address2: '',
				city: '',
				country: 'RS',
				state: '',
				postcode: '',
			  },
			  shipping: {
				shippable: false,
				is_cod: false,
				first_name: '',
				last_name: '',
				phone: '',
				company: '',
				address: '',
				address2: '',
				city: '',
				country: 'RS',
				state: '',
				postcode: '',
			  },
			  items: [{
				  "type": "product",
				  "sku": "",
				  "name": "",
				  "tax_percent": 20,
				  "price": "0.00",
				  "qty": 1,
				  "subtotal": 0,
				  "virtual": false,
				  "warehouse": "default",
				  "length": "",
				  "width": "",
				  "height": "",
				  "weight": ""
				}],
			  hpaylang: sessionStorage.language || 'rs',
			},
			SplitPayData: {},
			ShippingData: {},
			FiscalData: {}
		  };
  
  const [currentOrder, setCurrentOrder] = useState(() => {
	try{  
	
		if (order && order.id) {
		  
		  if(!order.Data)
			  order.Data = {};
		  
		  if(!order.Data.billing) order.Data.billing = {};
		  if(!order.Data.shipping) order.Data.shipping = {};
		  if(!order.Data.items) order.Data.items = order.id ? [] : [{
			  "type": "product",
			  "sku": "",
			  "name": "",
			  "tax_percent": 20,
			  "price": "0.00",
			  "qty": 1,
			  "subtotal": 0,
			  "virtual": false,
			  "warehouse": "default",
			  "length": "",
			  "width": "",
			  "height": "",
			  "weight": ""
			}];
			
		  return order; // Existing order for update
		} else {
		  // New order
		  let ord = {...EMPTY_ORDER};
		  if(window.orders_new_pbl){
			  ord.Data.is_pbl = 1;
			  if(localStorage.pbl_sms == "1")
				ord.Data.pbl_sms = 1;
			  ord.Data.pbl_offer_days = parseInt(localStorage.pbl_offer_days) || 7;
		  }
		  return ord;
		}
	}catch(ex){
		console.error(ex);
		return {error: ex.message, ex};
	}
  });
  
  const productSuggestions = () => {
	 try{ 
		 if(!window.__order_editor_suggestions){
			 window.__order_editor_suggestions = localStorage.getItem(PRODUCT_SUGGESTIONS_KEY) || {};
			 if(typeof window.__order_editor_suggestions === 'string' || window.__order_editor_suggestions instanceof String){
				 window.__order_editor_suggestions = JSON.parse(window.__order_editor_suggestions);
			 }
		 }  
		 return window.__order_editor_suggestions;
	 }catch(ex){
		 console.error(ex);
		 return {};
	 }
  };
  
  const setProductSuggestions = (data) => {
	 try{
		window.__order_editor_suggestions = data;
	    localStorage.setItem(PRODUCT_SUGGESTIONS_KEY, JSON.stringify(data));
	 }catch(ex){
		 console.error(ex);
	 } 
  };
  
  const [itemsValid, setItemsValid] = useState(true);
  const [currentTab, setCurrentTab] = useState(0); // 0 for "Editor", 1 for "JSON"
  const [shipToDifferentAddress, setShipToDifferentAddress] = useState(false);
  
  const [orderItemsDataValid, setOrderItemsDataValid] = useState(true);

  // Default InputLabelProps for consistent label positioning
  // This value (6px) might need slight adjustment based on overall font size and compactness
  const defaultInputLabelProps = {
    style: { transform: 'translate(14px, 6px) scale(0.75)' },
  };

  // Define default TextField props here, now including variant="standard",
  // smaller bottom margin, and whitesmoke background
  const defaultTextFieldProps = {
    fullWidth: true,
    size: "small",
    variant: "standard",
    sx: {
      mb: 0.5, // Reduced margin-bottom to shrink vertical space
      bgcolor: '#FEFEFE', // Light background color
      // You might need to adjust padding within the input if it looks too tight
      // For standard variant, padding is usually applied to InputProps.sx or InputProps.style
      '& .MuiInputBase-root': { // Targets the actual input element
        paddingTop: '0px',   // Adjust top padding to bring text closer to the label
        paddingBottom: '0px', // Adjust bottom padding for compactness
      },
      '& .MuiInputLabel-root': { // Targets the label when it's not shrunk
        //transform: 'translate(14px, 8px) scale(1)', // Adjust initial label position if needed
		  fontSize:"12px"
      },
	  '& .MuiInputBase-formControl':{
		  fontSize:"12px",
		  marginTop:"8px"
		  
	  }
    },
    InputLabelProps: defaultInputLabelProps,
  };
  
  const highlightedInputProps = {
	  sx:{
		  ...defaultTextFieldProps.sx,
		  '&':{
			  backgroundColor: "rgb(232 253 255 / 20%)"
		   },  
		   '& input':{
			   fontWeight:'bold'
		   }
	  }
  }; 
  
  const defaultSelectProps = {
	  variant: "standard",
	  fullWidth: true,
	  sx:{
		...defaultTextFieldProps.sx,  
		'& .MuiInput-root':{
			  marginTop: "5px"
		},
		'& .MuiInputLabel-standard':{
			paddingTop:'5px'
		}	
	  }
  };
  
  const transactionAmount = (transaction) => {
	  if(transaction){
		  if(transaction.Data){
			  try{
				  if (typeof transaction.Data === 'string' || transaction.Data instanceof String){
					  transaction.Data = JSON.parse(transaction.Data);
				  }
				  
				  if(transaction.Data.result){
					 return transaction.Data.result.payment_amount + " " + transaction.Data.result.payment_currency;
				  }
			  }catch(ex){
				  
			  }
		  }
	  }
	  return "";
  };


  // Effect to update currentOrder from props and initialize shipToDifferentAddress
  useEffect(() => {
    if (order) {
	  
	  if(order.error)
		return;
		
      setCurrentOrder(order);
      // Determine if shipping was different from billing initially
	  
	  try{
		  const isShippingDifferent =
			order.Data.shipping.first_name !== order.Data.billing.first_name ||
			order.Data.shipping.last_name !== order.Data.billing.last_name ||
			order.Data.shipping.phone !== order.Data.billing.phone ||
			order.Data.shipping.company !== order.Data.billing.company ||
			order.Data.shipping.address !== order.Data.billing.address ||
			order.Data.shipping.address2 !== order.Data.billing.address2 ||
			order.Data.shipping.city !== order.Data.billing.city ||
			order.Data.shipping.country !== order.Data.billing.country ||
			order.Data.shipping.state !== order.Data.billing.state ||
			order.Data.shipping.postcode !== order.Data.billing.postcode;
		  setShipToDifferentAddress(isShippingDifferent);
	  }catch(ex){
		  console.error(ex);
		  setShipToDifferentAddress(true);
	  }
    }
  }, [order]);

  // Effect to call onEditedOrderData whenever currentOrder changes
  useEffect(() => {
	if(order && order.error)
		return;

	if(currentOrder && currentOrder.error)
		return;	
	  
    if (onEditedOrderData) {
	  try{	
		  onEditedOrderData(currentOrder, {
			  DataValid: 
				orderItemsDataValid
				&& parseFloat(currentOrder.Amount) > 0
				&& (currentOrder.Data && currentOrder.Data.billing && ((currentOrder.Data.billing.email && validateEmail(currentOrder.Data.billing.email)) || currentOrder.Data.billing.phone)) 
				&& (currentOrder.Data && (!currentOrder.Data.allow_pm || currentOrder.Data.allow_pm.length > 0)),
			  IsPBL: isPayByLink
		  });
	  }catch(ex){
		  console.error(ex);
		  onEditedOrderData(currentOrder, {
			  DataValid: false,
			  sPBL: isPayByLink
		  });
	  }
    }
  }, [currentOrder/*, onEditedOrderData*/, orderItemsDataValid]);

  const handleFieldChange = (section, field, value) => {
    setCurrentOrder((prevOrder) => {
	  try{		
		  const newOrder = { ...prevOrder };
		  if (section === 'root') {
			if(value === undefined)
				delete newOrder[field];	
			else
				newOrder[field] = value;
		  }else if (section === 'Data') {
			if(value === undefined)
				delete newOrder.Data[field];
			else	
				newOrder.Data[field] = value;
			
		  } else if (newOrder.Data && newOrder.Data[section]) {
			newOrder.Data[section] = {
			  ...newOrder.Data[section],
			  [field]: value,
			};
			// If billing field changes and 'shipToDifferentAddress' is false, update shipping
			if (section === 'billing' && !shipToDifferentAddress) {
			  newOrder.Data.shipping = {
				...newOrder.Data.shipping, // Keep shippable and is_cod
				first_name: newOrder.Data.billing.first_name,
				last_name: newOrder.Data.billing.last_name,
				phone: newOrder.Data.billing.phone,
				company: newOrder.Data.billing.company,
				address: newOrder.Data.billing.address,
				address2: newOrder.Data.billing.address2,
				city: newOrder.Data.billing.city,
				country: newOrder.Data.billing.country,
				state: newOrder.Data.billing.state,
				postcode: newOrder.Data.billing.postcode,
			  };
			}
		  }
		  return newOrder;
	  }catch(ex){
		  console.error(ex);
		  return {};
	  }
    });
  };

  const handleDataChange = useCallback((newItems, prop, value) => {
    setCurrentOrder((prevOrder) => {
      const updatedOrder = {
        ...prevOrder,
        Data: {
          ...prevOrder.Data,
          items: newItems,
        },
      };

      // Calculate Amount and shippable status
      let totalAmount = 0;
      let isShippable = false;

      newItems.forEach((item) => {
        const itemPrice = parseFloat(item.price || 0);
        const itemQty = parseInt(item.qty || 0);
        const itemSubtotal = itemPrice * itemQty;
        item.subtotal = itemSubtotal; // Update subtotal in the item
        totalAmount += itemSubtotal;

        if (item.type === 'product' && !item.virtual) {
          isShippable = true;
        } else if (item.type === 'shipping') {
          isShippable = true;
        }
      });

      updatedOrder.Amount = parseFloat(totalAmount.toFixed(2));
      updatedOrder.Data.shipping.shippable = isShippable;

	  if(!order_editor_setting_suggest && prop && !/qty|subtotal/.test(prop)){	
		  // Save product suggestions
		  let dostore = false;
		  const newSuggestions = { ...productSuggestions() };
		  newItems.filter(item => item.name && (parseFloat(item.price) > 0 || item.type != "product"))
			.forEach(item => {
			  // Only add new suggestions if SKU doesn't exist
			  //if (!newSuggestions[item.sku]) {
			   if(newSuggestions[item.name] && newSuggestions[item.name][prop] == value)
				   return;
			   
			   dostore = true; 
			   newSuggestions[item.name] = { type: item.type, name: item.name, sku: item.sku || "",  price: item.price, tax_percent: item.tax_percent, virtual: item.virtual, length: item.length || "", width: item.width || "", height: item.height || "", weight: item.weight || "", warehouse: item.warehouse || "" };
			  //}
		   });
		   if(dostore){	
			  setProductSuggestions(newSuggestions);
		   }	
	  }
	  return updatedOrder;
    });
  }, []);


  const handleItemsValidityChange = useCallback((valid) => {
    setItemsValid(valid);
  }, []);

  const getWarehouses = useCallback(() => {
    try{
		const warehouses = window.hpay_get_warehouses();
		return Object.keys(warehouses).map(uid => ({
		  name: warehouses[uid].Name,
		  value: uid,
		}));
	}catch(ex){
		console.error(ex);
		return {};
	}
  }, []);

  const itemColumns = useMemo(() => {
	  let cols = {
		type: {
		  HeaderText: t('Type'),
		  Property: 'type',
		  Type: 'select',
		  Default: "product",
		  inputProps: {
			options: [
			  { name: t('Product'), value: 'product' },
			  { name: t('Shipping'), value: 'shipping' },
			  { name: t('Discount'), value: 'discount' },
			  { name: t('Fee'), value: 'fee' },
			],
		  },
		  required: true
		},
		sku: {
		  HeaderText: t('SKU'),
		  Property: 'sku',
		  Type: 'text',
		  required: false,
		  Default: "",
		  inputProps: (rowid, rowData, prop) => ({
			list: 'product-skus',
			onChange: (e, data) => {
			  //const rowid = e.target.getAttribute("rowid");	
			  const value = e.target.value;
			  const matchingName = !value ? null : Object.keys(productSuggestions()).find(name => productSuggestions()[name].sku === value);
			  if (matchingName) {
				const suggestion = productSuggestions()[matchingName];
				const newRowData = {
				  ...data[rowid],
				  ...suggestion
				};
				order_editor_setting_suggest = true;
				data[rowid] = newRowData;
				handleDataChange(data);
				order_editor_setting_suggest = false;
			  }else {
				data[rowid] = {...data[rowid], sku: value}; 
				handleDataChange(data);
			  }
			}
		  }),
		},
		name: {
		  HeaderText: t('Name'),
		  Property: 'name',
		  Type: 'text',
		  Default: "",
		  required: true,
		  inputProps: (rowid, rowData, prop) => ({
			list: 'product-names',
			onChange: (e, data) => {
			  //const rowid = e.target.getAttribute("rowid");
			  const value = e.target.value;
			  const suggestion = !value ? null : productSuggestions()[value];
			  if (suggestion) {
				const newRowData = {
				  ...data[rowid],
				  ...suggestion
				};
				order_editor_setting_suggest = true;
				data[rowid] = newRowData;
				handleDataChange(data);
				order_editor_setting_suggest = false;
			  } else {
				data[rowid] = {...data[rowid], name: value}; 
				handleDataChange(data);
			  }
			}
		  }),
		},
		tax_percent: {
		  HeaderText: t('Tax%'),
		  Property: 'tax_percent',
		  Type: 'select',
		  Default: 20,
		  inputProps: {
			options: [
			  { name: '0%', value: 0 },
			  { name: '10%', value: 10 },
			  { name: '20%', value: 20 },
			],
		  },
		  required: true
		},
		price: {
		  HeaderText: t('Unit Price'),
		  Property: 'price',
		  Type: 'number',
		  Default: "0.00",
		  inputProps: { step: 0.01 },
		  required: false,
		},
		qty: {
		  HeaderText: t('Qty'),
		  Property: 'qty',
		  Type: 'number',
		  Default: 1,
		  inputProps: { min: 0, step: 1 },
		  required: false,
		},
		subtotal: {
		  HeaderText: t('Subtotal'),
		  Property: 'subtotal',
		  Type: 'text', // Display only, not directly editable
		  Default: "0.00",
		  inputProps: { },
		  required: true
		},
		virtual: {
		  HeaderText: t('Virtual'),
		  Property: 'virtual',
		  Default: false,
		  Type: (rowid, rowData, prop) => rowData.type === 'product' ? 'checkbox' : 'text',
		  readonly: (rowid, rowData, prop) => rowData.type !== 'product',
		  inputProps: (rowid, rowData, prop) => rowData.type !== 'product' ? { disabled: true } : {},
		  required: false
		},
		warehouse: {
		  HeaderText: t('Warehouse'),
		  Property: 'warehouse',
		  Default: 'default',
		  Type: (rowid, rowData, prop) => rowData.type === 'product' && !rowData.virtual ? 'select' : 'text',
		  readonly: (rowid, rowData, prop) => rowData.type !== 'product' || rowData.virtual,
		  inputProps: (rowid, rowData, prop) => (rowData.type !== 'product' || rowData.virtual) ? { disabled: true } : { options: getWarehouses() },
		},
		// Dimensions - only for non-virtual products
		length: {
		  HeaderText: t('Length (cm)'),
		  Property: 'length',
		  Default: '',
		  Type: (rowid, rowData, prop) => rowData.type === 'product' && !rowData.virtual ? 'number' : 'text',
		  readonly: (rowid, rowData, prop) => rowData.type !== 'product' || rowData.virtual,
		  inputProps: (rowid, rowData, prop) => (rowData.type !== 'product' || rowData.virtual) ? { disabled: true } : { step: 0.01, min: 0 },
		},
		width: {
		  HeaderText: t('Width (cm)'),
		  Property: 'width',
		  Default: '',
		  Type: (rowid, rowData, prop) => rowData.type === 'product' && !rowData.virtual ? 'number' : 'text',
		  readonly: (rowid, rowData, prop) => rowData.type !== 'product' || rowData.virtual,
		  inputProps: (rowid, rowData, prop) => (rowData.type !== 'product' || rowData.virtual) ? { disabled: true } : { step: 0.01, min: 0 },
		},
		height: {
		  HeaderText: t('Height (cm)'),
		  Property: 'height',
		  Default: '',
		  Type: (rowid, rowData, prop) => rowData.type === 'product' && !rowData.virtual ? 'number' : 'text',
		  readonly: (rowid, rowData, prop) => rowData.type !== 'product' || rowData.virtual,
		  inputProps: (rowid, rowData, prop) => (rowData.type !== 'product' || rowData.virtual) ? { disabled: true } : { step: 0.01, min: 0 },
		},
		weight: {
		  HeaderText: t('Weight (g)'),
		  Property: 'weight',
		  Default: '',
		  Type: (rowid, rowData, prop) => rowData.type === 'product' && !rowData.virtual ? 'number' : 'text',
		  readonly: (rowid, rowData, prop) => rowData.type !== 'product' || rowData.virtual,
		  inputProps: (rowid, rowData, prop) => (rowData.type !== 'product' || rowData.virtual) ? { disabled: true } : { step: 0.01, min: 0 },
		},
	  };
	  
	  if(locked){
		  for(var cprop in cols){
			  if(cols.hasOwnProperty(cprop)){
				  cols[cprop].readonly = true;
			  }
		  }
	  }
	  
	  return cols;
	  
  },[locked]);

  const currencyOptions = [
    { value: 'RSD', label: 'RSD' },
    { value: 'MKD', label: 'MKD' },
    { value: 'BAM', label: 'BAM' },
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' },
    { value: 'CHF', label: 'CHF' },
    { value: 'TRY', label: 'TRY' },
    { value: 'ALL', label: 'ALL' },
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'rs', label: 'Serbian (Latin)' },
    { value: 'rs-cyr', label: 'Serbian (Cyrillic)' },
    { value: 'mk', label: 'Macedonian' },
    { value: 'hr', label: 'Croatian' },
    { value: 'me', label: 'Montenegrin' },
    { value: 'el', label: 'Greek' },
    { value: 'it', label: 'Italian' },
    { value: 'ru', label: 'Russian' },
    { value: 'fr', label: 'French' },
    { value: 'hu', label: 'Hungarian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'de', label: 'German' },
    { value: 'es', label: 'Spanish' },
    { value: 'nl', label: 'Dutch' },
    { value: 'si', label: 'Slovenian' },
    { value: 'ba', label: 'Bosnian' },
  ];

  const productSkuDatalist = Object.values(productSuggestions()).filter(itm => itm.sku).map(itm => (
    <option key={itm.sku} value={itm.sku}>{itm.sku}: {itm.name}</option>
  ));
  
  const productNameDatalist = Object.values(productSuggestions()).map(itm => (
    <option key={itm.name} value={itm.name}>{itm.sku}: {itm.name}</option>
  ));

  const _billing = (((currentOrder || {}).Data || {}).billing || {});
  const isBillingCompany = /^(1|true|yes)$/i.test(String(_billing.is_company)) || _billing.company || _billing.company_tax_id;

  const handleChangeTab = (event, newValue) => {
    setCurrentTab(newValue);
  };
  
  try{		
	  if(!currentOrder.Uid){
		  currentOrder = {...EMPTY_ORDER, ...currentOrder};
	  }	
  
	  return (
		<Paper sx={{ p: 0, m: 0 }}> {/* 0 Padding and 0 Margin */}
		  <Grid item xs={12} container spacing={2}>
			  <Grid item xs={12} md={5}>
			  <Stack direction='row' fullWidth>
			  <TextField
					  label={t('Order UID')}
					  variant="filled"
					  value={currentOrder.Uid || ''}
					  InputProps={{ readOnly: true }}
					/>
			  <TextField
				  label={t('Amount')}
				  value={currentOrder.Amount?.toFixed(2) || '0.00'}
				  variant="filled"
				  InputProps={{
					readOnly: true,
					startAdornment: <InputAdornment position="start">{currentOrder.Currency}</InputAdornment>,
				  }}
				/>
			   </Stack>	
			  </Grid>
			  <Grid item xs={12} md={4}>
					<Typography style={{minWidth:'30vw', fontWeight:'bold', fontSize:'.9em', fontStyle:'italic'}} variant="h6">{currentOrder.Status || ''}</Typography>
			  </Grid>
			  <Grid item xs={12} md={3}>
					<Tabs value={currentTab} onChange={handleChangeTab} aria-label="order editor tabs" sx={{ mb: 1 }}>
						<Tab label={t('Editor')} />
						<Tab label={t('Raw Data View')} />
					  </Tabs>
			  </Grid>
		  </Grid>
		  
		  {/* Editor Tab Content */}
		  {currentTab === 0 && (
			<Grid container spacing={0} sx={{ p: 0 }}> {/* Added padding to Grid content */}
			  {/* Order Details */}
			  <Grid item xs={12}>
				<Grid container spacing={1}>
				  <Grid item xs={12} md={6} >
					
					<TextField
					  label={t('Order Name')}
					  {...defaultTextFieldProps}
					  {...highlightedInputProps}
					  disabled={locked}
					  value={currentOrder.Name || ''}
					  onChange={(e) => handleFieldChange('root', 'Name', e.target.value)}
					/>
					
					<Stack sx={{
						display: {
							  xs: currentOrder.Data.sitedata ? 'block' : 'none'
							}
						  }}>
						<FormControlLabel
							control={
							  <Checkbox
								checked={!!currentOrder.Data.hpayedit}
								onChange={(e) => {
									setLocked(!e.target.checked);
									handleFieldChange('Data', 'hpayedit', e.target.checked ? 1 : 0)
								}}
								size="small"
							  />
							}
							{...defaultTextFieldProps}
							labelPlacement="start"
							label={t('This order should be managed on-site. Override?')}
							sx={{ mt: 0, mb: 0, "& *":{fontWeight:"bold", fontStyle:"italic"} }}
						  />
					</Stack>
					
					<TextField
					label={t('Email')}
					disabled={locked}
					{...defaultTextFieldProps}
					{...highlightedInputProps}
					value={currentOrder.Data.billing.email || ''}
					onChange={(e) => handleFieldChange('billing', 'email', e.target.value)}
				  />
				  
				  </Grid>
				  <Grid item xs={12} md={6}>
				   
					<TextField
					  select
					  label={t('Currency')}
					  disabled={locked}
					  {...defaultSelectProps}
					  value={currentOrder.Currency || 'EUR'}
					  onChange={(e) => handleFieldChange('root', 'Currency', e.target.value)}
					>
					  {currencyOptions.map((option) => (
						<MenuItem key={option.value} value={option.value}>
						  {option.label}
						</MenuItem>
					  ))}
					</TextField>
					
					<TextField
					  select
					  label={t('Langauge')}
					  disabled={locked}
					  {...defaultSelectProps}
					  value={currentOrder.Data.hpaylang || "rs"}
					  onChange={(e) => handleFieldChange('Data', 'hpaylang', e.target.value)}
					>
					  {languageOptions.map((option) => (
						<MenuItem key={option.value} value={option.value}>
						  {option.label}
						</MenuItem>
					  ))}
					</TextField>
				  </Grid>
				</Grid>
				{/* Status field below the two columns */}
				
				
				
			  </Grid>

			  {/* Billing and Shipping Information in one row */}
			  <Grid item xs={12} container spacing={2}>
				{/* Billing Information */}
				<Grid item xs={12} md={6}>
				  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between">
				  <Typography variant="h7" gutterBottom style={{fontStyle:"italic"}}>{t('Billing')}</Typography>
				  <FormControlLabel
						control={
						  <Checkbox
							checked={isBillingCompany}
							disabled={locked}
							onChange={(e) => handleFieldChange('billing', 'is_company', e.target.checked ? 1 : 0)}
							size="small"
						  />
						}
						{...defaultTextFieldProps}
						label={t('Is Company?')}
						sx={{ mt: 0, mb: 0 }}
					  />
				  <FormControlLabel
						control={
						  <Checkbox
							checked={shipToDifferentAddress}
							onChange={(e) => setShipToDifferentAddress(e.target.checked)}
							size="small"
						  />
						}
						{...defaultTextFieldProps}
						disabled={locked}
						label={t('Ship to different address?')}
						sx={{ mt: 0, mb: 0 }}
					  />
				  	  
				  </Stack>
				  
				  <Stack direction="row" spacing={1}>
					  <TextField
						label={t('First Name')}
						{...defaultTextFieldProps}
						disabled={locked}
						value={currentOrder.Data.billing.first_name || ''}
						onChange={(e) => handleFieldChange('billing', 'first_name', e.target.value)}
					  />
					  <TextField
						label={t('Last Name')}
						{...defaultTextFieldProps}
						value={currentOrder.Data.billing.last_name || ''}
						disabled={locked}
						onChange={(e) => handleFieldChange('billing', 'last_name', e.target.value)}
					  />
				  </Stack>
				  
				  {isBillingCompany && (
					<Stack direction="row" spacing={1}>
					  <TextField
						label={t('Company Name')}
						{...defaultTextFieldProps}
						value={currentOrder.Data.billing.company || ''}
						disabled={locked}
						onChange={(e) => handleFieldChange('billing', 'company', e.target.value)}
					  />
					  <TextField
						label={t('Company Tax ID')}
						{...defaultTextFieldProps}
						value={currentOrder.Data.billing.company_tax_id || ''}
						disabled={locked}
						onChange={(e) => handleFieldChange('billing', 'company_tax_id', e.target.value)}
					  />
					  <TextField
						label={t('Company Registration ID')}
						{...defaultTextFieldProps}
						value={currentOrder.Data.billing.company_reg_id || ''}
						disabled={locked}
						onChange={(e) => handleFieldChange('billing', 'company_reg_id', e.target.value)}
					  />
					</Stack>
				  )}
				  <Stack direction="row" spacing={1}>
					  <TextField
						label={t('Address Line 1')}
						{...defaultTextFieldProps}
						value={currentOrder.Data.billing.address || ''}
						disabled={locked}
						onChange={(e) => handleFieldChange('billing', 'address', e.target.value)}
					  />
					  <TextField
						label={t('Address Line 2')}
						{...defaultTextFieldProps}
						value={currentOrder.Data.billing.address2 || ''}
						disabled={locked}
						onChange={(e) => handleFieldChange('billing', 'address2', e.target.value)}
					  />
				  </Stack>
				  <Stack direction="row" spacing={1}>
					  <TextField
						label={t('Postcode')}
						{...defaultTextFieldProps}
						value={currentOrder.Data.billing.postcode || ''}
						disabled={locked}
						onChange={(e) => handleFieldChange('billing', 'postcode', e.target.value)}
					  />
					  <TextField
						label={t('City')}
						{...defaultTextFieldProps}
						value={currentOrder.Data.billing.city || ''}
						disabled={locked}
						onChange={(e) => handleFieldChange('billing', 'city', e.target.value)}
					  />
				  </Stack>
				  
				  <Stack direction="row" spacing={1}>
				  <TextField
						label={t('Country')}
						{...defaultTextFieldProps}
						value={currentOrder.Data.billing.country || ''}
						onChange={(e) => handleFieldChange('billing', 'country', e.target.value)}
					  />
					  <TextField
						label={t('State')}
						{...defaultTextFieldProps}
						value={currentOrder.Data.billing.state || ''}
						disabled={locked}
						onChange={(e) => handleFieldChange('billing', 'state', e.target.value)}
					  />
				  </Stack>
				  
				  <TextField
					label={t('Phone')}
					{...defaultTextFieldProps}
					value={currentOrder.Data.billing.phone || ''}
					disabled={locked}
					onChange={(e) => handleFieldChange('billing', 'phone', e.target.value)}
				  />
				</Grid>

				{/* Shipping Information */}
				<Grid item xs={12} md={6} >
				  
				  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between">
					  <Typography variant="h7" gutterBottom style={{fontStyle:"italic"}} sx={{
					display: {
					  xs: shipToDifferentAddress ? 'block' : 'none', // Hide on xs-md if unchecked
					  md: 'block', // Always show on md and up
					}
				  }}>{t('Shipping')}</Typography>
					  <FormControlLabel
						control={
						  <Checkbox
							checked={!!currentOrder.Data.shipping.is_cod}
							disabled={locked}
							onChange={(e) => handleFieldChange('shipping', 'is_cod', e.target.checked)}
							size="small"
						  />
						}
						{...defaultTextFieldProps}
						label={t('Cash on Delivery')}
						sx={{ mt: 0, mb: 0 }}
					  />
				  </Stack>
				  <Stack direction="column" spacing={0} sx={{
					display: {
					  xs: shipToDifferentAddress ? 'block' : 'none', // Hide on xs-md if unchecked
					  md: 'block', // Always show on md and up
					}
				  }}>
				  
					  <Stack direction="row" spacing={1}>
						  <TextField
							label={t('First Name')}
							{...defaultTextFieldProps}
							disabled={!shipToDifferentAddress || locked}
							value={shipToDifferentAddress ? currentOrder.Data.shipping.first_name : currentOrder.Data.billing.first_name || ''}
							onChange={(e) => handleFieldChange('shipping', 'first_name', e.target.value)}
							InputProps={{ readOnly: !shipToDifferentAddress }}
						  />
						  <TextField
							label={t('Last Name')}
							{...defaultTextFieldProps}
							disabled={!shipToDifferentAddress || locked}
							value={shipToDifferentAddress ? currentOrder.Data.shipping.last_name : currentOrder.Data.billing.last_name || ''}
							onChange={(e) => handleFieldChange('shipping', 'last_name', e.target.value)}
							InputProps={{ readOnly: !shipToDifferentAddress }}
						  />
					  </Stack>
					  
					  <Stack direction="row" spacing={1}>
						  <TextField
							label={t('Address Line 1')}
							{...defaultTextFieldProps}
							disabled={!shipToDifferentAddress || locked}
							value={shipToDifferentAddress ? currentOrder.Data.shipping.address : currentOrder.Data.billing.address || ''}
							onChange={(e) => handleFieldChange('shipping', 'address', e.target.value)}
							InputProps={{ readOnly: !shipToDifferentAddress }}
						  />
						  <TextField
							label={t('Address Line 2')}
							{...defaultTextFieldProps}
							disabled={!shipToDifferentAddress || locked}
							value={shipToDifferentAddress ? currentOrder.Data.shipping.address2 : currentOrder.Data.billing.address2 || ''}
							onChange={(e) => handleFieldChange('shipping', 'address2', e.target.value)}
							InputProps={{ readOnly: !shipToDifferentAddress }}
						  />
					  </Stack>
					  
					  <Stack direction="row" spacing={1}>
						<TextField
							label={t('Postcode')}
							{...defaultTextFieldProps}
							disabled={!shipToDifferentAddress || locked}
							value={shipToDifferentAddress ? currentOrder.Data.shipping.postcode : currentOrder.Data.billing.postcode || ''}
							onChange={(e) => handleFieldChange('shipping', 'postcode', e.target.value)}
							InputProps={{ readOnly: !shipToDifferentAddress }}
						  />
						<TextField
							label={t('City')}
							{...defaultTextFieldProps}
							disabled={!shipToDifferentAddress || locked}
							value={shipToDifferentAddress ? currentOrder.Data.shipping.city : currentOrder.Data.billing.city || ''}
							onChange={(e) => handleFieldChange('shipping', 'city', e.target.value)}
							InputProps={{ readOnly: !shipToDifferentAddress }}
						  />
					  </Stack>	
					  
					  <Stack direction="row" spacing={1}>
						 
						  <TextField
							label={t('Country')}
							{...defaultTextFieldProps}
							disabled={!shipToDifferentAddress || locked}
							value={shipToDifferentAddress ? currentOrder.Data.shipping.country : currentOrder.Data.billing.country || ''}
							onChange={(e) => handleFieldChange('shipping', 'country', e.target.value)}
							InputProps={{ readOnly: !shipToDifferentAddress }}
						  />
						  <TextField
							label={t('State')}
							{...defaultTextFieldProps}
							disabled={!shipToDifferentAddress || locked}
							value={shipToDifferentAddress ? currentOrder.Data.shipping.state : currentOrder.Data.billing.state || ''}
							onChange={(e) => handleFieldChange('shipping', 'state', e.target.value)}
							InputProps={{ readOnly: !shipToDifferentAddress }}
						  />
					  </Stack>
					    
					  
					  <TextField
						label={t('Phone')}
						{...defaultTextFieldProps}
						disabled={!shipToDifferentAddress || locked}
						value={shipToDifferentAddress ? currentOrder.Data.shipping.phone : currentOrder.Data.billing.phone || ''}
						onChange={(e) => handleFieldChange('shipping', 'phone', e.target.value)}
						InputProps={{ readOnly: !shipToDifferentAddress }}
					  />
				 </Stack> 
				</Grid>
				<Grid item xs={12} style={{paddingTop:5}}>
				      <TextField
						label={t('Note')}
						{...defaultTextFieldProps}
						multiline
						rows={1}
						disabled={locked}
						value={currentOrder.Data.shipping.note || ''}
						onChange={(e) => handleFieldChange('shipping', 'note', e.target.value)}
					  />
				</Grid>	  
			  </Grid>
			</Grid>
		  )}

		  {/* Order Items Table (visible in Editor tab) */}
		  {currentTab === 0 && (
			<Box sx={{ mt: 0, p: 0 }}> {/* Added padding to items table */}
			  <Typography variant="h7" gutterBottom style={{fontStyle:"italic"}} >{t('Order Items')}</Typography>
			  <ObjectTable
				id="order_items_editor"  
				Data={currentOrder.Data.items}
				Columns={itemColumns}
				onDataChange={handleDataChange}
				onDataState={(dataState) => {
					if(dataState && dataState.DataValid){
						setOrderItemsDataValid(true);
					}else{
						setOrderItemsDataValid(false);
					}
				}}
				AllowAddRemoveOffset={locked ? "NO" :  0}
				//PrimaryIdentifier="0"
				ColumnsSelector={Object.values(itemColumns).filter(c=>c.hasOwnProperty("required")).map(c => c.Property )}
			  />
			  {!itemsValid && (
				<Typography color="error" variant="body2" sx={{ mt: 1 }}>
				  {t('Please ensure all required item fields are filled correctly.')}
				</Typography>
			  )}
			</Box>
		  )}
		  
		  {(currentTab === 0 && currentOrder.Transactions && currentOrder.Transactions.length) ? (
			<Box sx={{ mt: 0, p: 0 }}> {/* Added padding to items table */}
			  <Typography variant="h7" gutterBottom style={{fontStyle:"italic"}} >{t('Transactions')}</Typography>
			  <ObjectTable
				id="order_transactions"  
				Data={ (currentOrder.Transactions).map((t, key ) => {
					return {
						Uid: t.Uid,
						CreatedAt: String(t.CreatedAt).substring(0,16).replace("T"," "),
						TransactionAmount: transactionAmount(t),
						Status: t.Status,
						ViewLink: "/transactions/" + t.id
					};	
				})}
				PrimaryIdentifier={"Uid"}
				Columns={{
					"Uid": {Property:"Uid", HeaderText: t("Uid"), readonly: true},
					"CreatedAt": {Property:"CreatedAt", HeaderText: t("Created On"), readonly: true},
					"TransactionAmount": {Property:"TransactionAmount", HeaderText: t("Amount"), readonly: true},
					"Status": {Property:"Status", HeaderText: t("Status"), readonly: true},
					"ViewLink": {Property:"ViewLink", Type:"link",  HeaderText: t(" ")}
				}}
				onDataChange={()=>{}}
				AllowAddRemoveOffset={"NO"}
			  />
			  {!itemsValid && (
				<Typography color="error" variant="body2" sx={{ mt: 1 }}>
				  {t('Please ensure all required item fields are filled correctly.')}
				</Typography>
			  )}
			</Box>
		  ): null}
		  
		  {(currentTab === 0 && isPayByLink)? (
			<Box sx={{ mt: 0, p: 0 }}> {/* Added padding to items table */}
			   <Typography variant="h7" gutterBottom style={{fontStyle:"italic"}}>{t('Pay By Link Options')}</Typography>
			   <Stack direction="row" spacing={5} >
					<TextField
					  label={t('Offer validity days') + ((currentOrder.id && currentOrder.CreatedAt) ? (": " + dateWithDayOffset(currentOrder.CreatedAt, currentOrder.Data.pbl_offer_days) ) : "")}
					  {...defaultTextFieldProps}
					  {...highlightedInputProps}
					  value={ currentOrder.Data.pbl_offer_days }
					  style={{maxWidth: 180}}
					  onChange={(e) => {
						  let val = String(e.target.value).replace(/[^\d]/g,'');
						  handleFieldChange('Data', 'pbl_offer_days', val );
						  try{
							if(val)  
								localStorage["pbl_offer_days"] = val;
						  }catch(ex){}
					  }}
					/>
					{currentOrder.Data.pbl_notify ? <Typography style={{fontSize:"60%", lineHeight:'90%', fontStyle:'italic', textAlign:"left"}} variant="span">{t("Sent") + ": " + currentOrder.Data.pbl_notify}</Typography> : null}
					<TextField
					  label={t('Pay by link URL')}
					  {...defaultTextFieldProps}
					  {...highlightedInputProps}
					  disabled={!currentOrder.id}
					  value={configData.CLIENT_URL.replace("/clientpay","").replace(/\/$/,'') + "/pbl/" + sessionStorage.site_id + "-" + addChecksum27(currentOrder.Uid.replace(/^PBL/,''))}
					/>
					<Button title={t("Copy to clipboard...")} onClick={() => {
						if(!currentOrder.id){
							navigator.clipboard.writeText("-- you forgot to save the order first! --");
						}else{
							navigator.clipboard.writeText(configData.CLIENT_URL.replace("/clientpay","").replace(/\/$/,'') + "/pbl/" + sessionStorage.site_id + "-" + addChecksum27(currentOrder.Uid.replace(/^PBL/,'')));
						}
					}} size='small' style={{minWidth: "20px"}} key="details-copy"  variant="filled" >📄</Button>
			   </Stack>
			   {/^(?:(?:381|00381)6[0-9]\d{6,7}|0?6[0-9]\d{6,7})$/.test(String(currentOrder.Data.billing.phone).replace(/[^\d\+]/g,'')) ? 
				<FormControlLabel
					key={"pbl-sms"}
					control={
					  <Checkbox
						checked={!!currentOrder.Data.pbl_sms}
						onChange={(e) => {
							handleFieldChange('Data', 'pbl_sms', e.target.checked ? 1 : undefined);
							try{
								localStorage["pbl_sms"] = e.target.checked ? 1 : 0;
							}catch(ex){}
						}}
						size="small"
					  />
					}
					labelPlacement='start'
					{...defaultTextFieldProps}
					label={t("Also notify via SMS") + (" (" + configData.SMS_PRICE_RSD + t("RSD /SMS") + ")")}
					sx={{ mt: 0, mb: 0, padding:0, "& > *":{padding:'1px', fontSize:'80%', fontWeight:'bold'} }}
				  />
			  : null}
			   <Grid container spacing={0} wrap="nowrap" >
				<Grid item xs={6} md={6}>
					<Stack direction="column" spacing={0}>
					<Typography variant="h7" gutterBottom style={{fontStyle:"italic", fontSize:"80%"}}>{t('Payment methods')}</Typography>
					{Object.values(SitePaymentMethods || {}).filter(m => m.Enabled && !m.Hidden).map((m, index) => (
						<FormControlLabel
							key={"pbl-pm-" + index}
							control={
							  <Checkbox
							    checked={!currentOrder.Data.allow_pm || currentOrder.Data.allow_pm.includes(m.Uid)}
								onChange={(e) => {
									let allow_pm = currentOrder.Data.allow_pm;
									let allpm = Object.values(SitePaymentMethods || {}).filter(pm => pm.Enabled && !pm.Hidden).map(pm => pm.Uid);
									if(!allow_pm){
										allow_pm = allpm;
									}else if(allow_pm.length == allpm.length){
										allow_pm = undefined;
									}
									if(allow_pm){
										if(e.target.checked){
											if(!allow_pm.includes(m.Uid)){
												allow_pm.push(m.Uid);
											}
										}else{
											let ind = allow_pm.indexOf(m.Uid);
											if(ind > -1){
												allow_pm.splice(ind,1);
											}
										}
									}
									handleFieldChange('Data', 'allow_pm', allow_pm);
								}}
								size="small"
							  />
							}
							{...defaultTextFieldProps}
							label={m.Data.parameters.Name}
							sx={{ mt: 0, mb: 0, padding:0, "& > *":{padding:'1px', fontSize:'80%'} }}
						  />
					))}
					</Stack>
				</Grid>
				<Grid item xs={6} md={6}>
					<Stack direction="column" spacing={0}>
					<Typography variant="h7" gutterBottom style={{fontStyle:"italic", fontSize:"80%"}}>{t('Shipping methods')}</Typography>
					{Object.values(SiteShippingMethods || {}).filter(m => m.Enabled && !m.Hidden).map((m, index) => (
						<FormControlLabel
							key={"pbl-sm-" + index}
							control={
							  <Checkbox
							    checked={!currentOrder.Data.allow_sm || currentOrder.Data.allow_sm.includes(m.Uid)}
								onChange={(e) => {
									let allow_sm = currentOrder.Data.allow_sm;
									let allsm = Object.values(SiteShippingMethods || {}).filter(sm => sm.Enabled && !sm.Hidden).map(sm => sm.Uid);
									if(!allow_sm){
										allow_sm = allsm;
									}else if(allow_sm.length == allsm.length){
										allow_sm = undefined;
									}
									if(allow_sm){
										if(e.target.checked){
											if(!allow_sm.includes(m.Uid)){
												allow_sm.push(m.Uid);
											}
										}else{
											let ind = allow_sm.indexOf(m.Uid);
											if(ind > -1){
												allow_sm.splice(ind,1);
											}
										}
									}
									handleFieldChange('Data', 'allow_sm', allow_sm);
								}}
								size="small"
							  />
							}
							{...defaultTextFieldProps}
							label={m.Data.parameters.Name}
							sx={{ mt: 0, mb: 0, padding:0, "& > *":{padding:'1px', fontSize:'80%'} }}
						  />
					))}
					</Stack>
				</Grid>
			  </Grid>
			  
			</Box>
		  ): null}
		  
		  {/* JSON View Tab Content */}
		  {currentTab === 1 && (
			<Box sx={{ mt: 0, bgcolor: 'background.paper', p: 2, borderRadius: 1, overflowX: 'auto', border: '1px solid #e0e0e0' }}>
			  <pre style={{ margin: 0, padding: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
				{JSON.stringify({order: currentOrder}, null, 2)}
			  </pre>
			</Box>
		  )}

		  {/* Datalists for product suggestions */}
		  <datalist id="product-skus">
			{productSkuDatalist}
		  </datalist>
		  <datalist id="product-names">
			{productNameDatalist}
		  </datalist>
		</Paper>
	  );
  }catch(ex){
	  console.error(ex);
	  console.error("currentOrder:", currentOrder);
	  return (<p>{ex.message}</p>);
  }
}